// import

import type {ParsedUrlQueryInput} from 'querystring';
import qs from 'querystring';

import type {UrlNode} from 'libs/model';
import type {socialIcons} from 'modules/Icon';

// import {facebook, twitter, tumblr, pinterest, pocket, reddit, linkedin, wordpress} from 'brand-colors';

// types

type BasicMeta = UrlNode['meta'];
type SocialKey = keyof typeof socialIcons | 'email';

type GetLink = (meta: BasicMeta) => {
  name: string;
  icon: SocialKey;
  color: string | null;
  link: string;
};

// vars

const tagRx = /<\/?[^>]+(?:>|$)/g;
const siteUrl = process.env.RESOLVED_URL;
const appId = process.env.FACEBOOK_APP_ID;

const facebook = '#3b5998';
const twitter = '#1da1f2';
const tumblr = '#35465c';
const pinterest = '#bd081c';
const pocket = '#ef4056';
const reddit = '#ff4500';
const linkedin = '#0077b5';
const wordpress = '#21759b';

// fns

const stripTags = (str: string = '') =>
  str.replace(tagRx, '').trim();

const createUrl = (path: string, query: ParsedUrlQueryInput = {}) => {
  const parsed = qs.stringify(query);

  return path + (parsed ? `?${parsed}` : '');
};

// export

export const getLinkProps: Record<string, GetLink> = {
  facebook: (meta: BasicMeta) => ({
    name: 'Facebook',
    icon: 'facebook',
    color: facebook,
    link: createUrl('https://www.facebook.com/dialog/share', {
      href: `${siteUrl}${meta.link}`,
      display: 'page',
      redirect_uri: `${siteUrl}${meta.link}`,
      app_id: appId,
    }),
  }),

  twitter: (meta: BasicMeta) => ({
    name: 'Twitter',
    icon: 'twitter',
    color: twitter,
    link: createUrl('https://twitter.com/intent/tweet', {
      source: `${siteUrl}${meta.link}`,
      text: `"${meta.title}"`,
      via: 'Site_Arcade',
    }),
  }),

  tumblr: (meta: BasicMeta) => ({
    name: 'Tumblr',
    icon: 'tumblr',
    color: tumblr,
    link: createUrl('http://www.tumblr.com/share', {
      v: 3,
      u: `${siteUrl}${meta.link}`,
      t: meta.title,
    }),
  }),

  pinterest: (meta: BasicMeta) => ({
    name: 'Pinterest',
    icon: 'pinterest',
    color: pinterest,
    link: createUrl('http://pinterest.com/pin/create/button/', {
      url: `${siteUrl}${meta.link}`,
      media: `${siteUrl}${meta.bannerImage}`,
      description: `${meta.title}\n\n"${stripTags(meta.seoDescription)}"`,
    }),
  }),

  pocket: (meta: BasicMeta) => ({
    name: 'Pocket',
    icon: 'pocket',
    color: pocket,
    link: createUrl('https://getpocket.com/save', {
      url: `${siteUrl}${meta.link}`,
      title: meta.title,
    }),
  }),

  reddit: (meta: BasicMeta) => ({
    name: 'Reddit',
    icon: 'reddit',
    color: reddit,
    link: createUrl('http://www.reddit.com/submit', {
      url: `${siteUrl}${meta.link}`,
      title: meta.title,
    }),
  }),

  linkedin: (meta: BasicMeta) => ({
    name: 'LinkedIn',
    icon: 'linkedin',
    color: linkedin,
    link: createUrl('http://www.linkedin.com/shareArticle', {
      mini: true,
      url: `${siteUrl}${meta.link}`,
      source: siteUrl,
      title: meta.title,
      summary: stripTags(meta.seoDescription),
    }),
  }),

  wordpress: (meta: BasicMeta) => ({
    name: 'Wordpress',
    icon: 'wordpress',
    color: wordpress,
    link: createUrl('http://wordpress.com/press-this.php', {
      u: `${siteUrl}${meta.link}`,
      i: `${siteUrl}${meta.bannerImage}`,
      t: meta.title,
      s: stripTags(meta.seoDescription),
    }),
  }),

  email: (meta: BasicMeta) => ({
    name: 'Email',
    icon: 'email',
    color: null,
    link: createUrl('mailto:', {
      subject: `Check this out: "${meta.title}"`,
      body: `Hey, I thought you might like this...\n\n"${meta.title}"\n${siteUrl}${meta.link}`,
    }),
  }),
};
